import { useEffect } from 'react';

import {
  ArrowTurnBackwardIcon,
  InformationCircleIcon,
  PencilEdit02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { countries } from 'constants/countries';
import patternValidators, { emailFieldError } from 'helpers/validators/pattern-validators';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Input, InputGroup, Select } from 'components/FormComponents';

import {
  hasCompleteBillingDetails,
  pickBillingDetails,
  usesCompanyBillingDetails,
} from '../../billing-details';

const localityKey = country => {
  if (country === 'Australia') return 'stateTerritory';
  if (country === 'United States of America') return 'state';
  if (country === 'New Zealand') return 'cityTown';
  return 'townCity';
};

const normalise = value => (value || '').trim().toLowerCase();

const SectionLabel = ({ children }) => (
  <h4 className="m-0 mb-3 text-lg font-medium uppercase tracking-wide text-text-tertiary">
    {children}
  </h4>
);

const CompanyValueHint = ({ value }) => {
  const { t } = useTranslation();

  return (
    <p className="m-0 mt-1.5 text-lg text-text-tertiary">
      {t('company.tabs.billing.form.companyValue', { value })}
    </p>
  );
};

const BillingDetailsStatus = ({ companyEntity, matchesCompany }) => {
  const { t } = useTranslation();

  if (!hasCompleteBillingDetails(companyEntity)) {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-lg bg-surface-secondary px-4 py-3">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={18}
          className="mt-0.5 shrink-0 text-text-secondary"
        />
        <span className="text-xl text-text-secondary">
          {t('company.tabs.billing.form.companyMissing')}
        </span>
      </div>
    );
  }

  if (matchesCompany) {
    return (
      <div className="mb-6 flex items-start gap-3 rounded-lg bg-surface-secondary px-4 py-3">
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={18}
          className="mt-0.5 shrink-0 text-text-secondary"
        />
        <div className="text-xl text-text-secondary">
          <div className="font-semibold text-text-primary">
            {t('company.tabs.billing.form.usingCompany')}
          </div>
          {t('company.tabs.billing.form.usingCompanyHint')}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-start gap-3 rounded-lg bg-primary-5/[0.08] px-4 py-3">
      <HugeiconsIcon icon={PencilEdit02Icon} size={18} className="mt-0.5 shrink-0 text-primary-5" />
      <div className="text-xl text-text-secondary">
        <div className="font-semibold text-primary-5">
          {t('company.tabs.billing.form.different')}
        </div>
        {t('company.tabs.billing.form.differentHint')}
      </div>
    </div>
  );
};

export const BillingAddressForm = ({
  billingEntity,
  companyEntity,
  onClose = () => {},
  handleUpdate = () => {},
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    reset,
    register,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({ mode: 'onTouched', reValidateMode: 'onChange', defaultValues: billingEntity });

  useEffect(() => {
    reset(billingEntity || {});
  }, [billingEntity, reset]);

  const values = watch();
  const country = values.country || '';
  const locality = t(`company.tabs.billing.form.locality.${localityKey(country)}`);
  const setField = name => event =>
    setValue(name, event.target.value, { shouldDirty: true, shouldValidate: true });

  const comparesToCompany = !!companyEntity && hasCompleteBillingDetails(companyEntity);
  const matchesCompany = usesCompanyBillingDetails(values, companyEntity);
  const companyValueFor = field =>
    comparesToCompany && normalise(values[field]) !== normalise(companyEntity[field])
      ? companyEntity[field]
      : null;

  const resetToCompanyDetails = () =>
    Object.entries(pickBillingDetails(companyEntity)).forEach(([field, value]) =>
      setValue(field, value || '', { shouldDirty: true, shouldValidate: true }),
    );

  return (
    <form onSubmit={handleSubmit(handleUpdate)}>
      {companyEntity && (
        <BillingDetailsStatus companyEntity={companyEntity} matchesCompany={matchesCompany} />
      )}

      <SectionLabel>{t('company.tabs.billing.form.invoiceRecipient')}</SectionLabel>
      <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
        <InputGroup title={t('company.tabs.billing.form.entityName')}>
          <Input
            name="invoice_entity_name"
            placeholder={t('company.tabs.billing.form.entityNamePlaceholder')}
            register={register}
            validators={{ required: true }}
            error={errors?.invoice_entity_name?.type}
            inputProps={{
              value: values.invoice_entity_name || '',
              onChange: setField('invoice_entity_name'),
            }}
          />
          {companyValueFor('invoice_entity_name') && (
            <CompanyValueHint value={companyValueFor('invoice_entity_name')} />
          )}
        </InputGroup>
        <InputGroup title={t('company.tabs.billing.form.invoiceEmail')}>
          <Input
            name="email"
            placeholder={t('company.tabs.billing.form.invoiceEmailPlaceholder')}
            register={register}
            validators={{
              required: t('company.tabs.billing.form.invoiceEmailRequired'),
              ...patternValidators.email,
            }}
            error={emailFieldError(errors?.email)}
            inputProps={{ value: values.email || '', onChange: setField('email') }}
          />
          {companyValueFor('email') && <CompanyValueHint value={companyValueFor('email')} />}
        </InputGroup>
      </div>

      <div className="mt-2">
        <SectionLabel>{t('company.tabs.billing.form.billingAddress')}</SectionLabel>
      </div>
      <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-3">
        <InputGroup title={t('company.tabs.billing.form.country')}>
          <Select
            name="country"
            register={register}
            value={country}
            onChange={setField('country')}
            placeholder={t('company.tabs.billing.form.countryPlaceholder')}
            useDefault={false}
            validators={{ required: true }}
            error={errors?.country?.type}>
            {countries.map(countryOption => (
              <Select.Item key={countryOption.value} value={countryOption.value}>
                {countryOption.name}
              </Select.Item>
            ))}
          </Select>
          {companyValueFor('country') && <CompanyValueHint value={companyValueFor('country')} />}
        </InputGroup>
        <InputGroup title={locality}>
          <Input
            name="region"
            placeholder={t('company.tabs.billing.form.regionPlaceholder', {
              locality: locality.toLowerCase(),
            })}
            register={register}
            validators={{ required: true }}
            error={errors?.region?.type}
            inputProps={{ value: values.region || '', onChange: setField('region') }}
          />
          {companyValueFor('region') && <CompanyValueHint value={companyValueFor('region')} />}
        </InputGroup>
        <InputGroup title={t('company.tabs.billing.form.postcode')}>
          <Input
            name="post_code"
            placeholder={t('company.tabs.billing.form.postcodePlaceholder')}
            register={register}
            validators={{ required: true }}
            error={errors?.post_code?.type}
            inputProps={{ value: values.post_code || '', onChange: setField('post_code') }}
          />
          {companyValueFor('post_code') && (
            <CompanyValueHint value={companyValueFor('post_code')} />
          )}
        </InputGroup>
      </div>

      <div className="-mx-[16px] mt-4 flex flex-wrap items-center gap-3 border-t border-border-primary px-[16px] pt-6">
        {comparesToCompany && !matchesCompany && (
          <Button type="secondary" onClick={resetToCompanyDetails}>
            <HugeiconsIcon icon={ArrowTurnBackwardIcon} size={16} className="mr-2" />
            {t('company.tabs.billing.form.resetToCompany')}
          </Button>
        )}
        <div className="ml-auto flex gap-3">
          <Button type="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button submit disabled={!isDirty}>
            {t('company.tabs.billing.form.saveChanges')}
          </Button>
        </div>
      </div>
    </form>
  );
};
